import React from 'react';
import styled, { css } from 'styled-components';
import {
    LicenceType,
    type TieredLicence,
    type Tier,
    useActiveTieredLicensingsForLicenceType,
} from '../hooks/useActiveLicenceTypes';
import { Select, SelectItem } from './Select';
import notUnlimited from '../utils/notUnlimited';
import useCartQuery from '../hooks/useCartQuery';

const Container = styled.div<{
    $invertSelectColor?: boolean;
}>`
    font-feature-settings: 'tnum';
    display: flex;
    flex-wrap: wrap;
    place-content: flex-end flex-end;
    text-align: right;

    /*
    This is crucial to allow the Select text to truncate with ellipsis!
    See: https://css-tricks.com/flexbox-truncated-text/
    */
    min-width: 0;

    ${({ $invertSelectColor }): ReturnType<typeof css> | null =>
        $invertSelectColor
            ? css`
                  --lozengeBackgroundColor: var(--backgroundColor);
                  --lozengeHoverColor: var(--backgroundColor);
                  --lozengeBackgroundHoverColor: var(--foregroundColor);
              `
            : null};
`;

const TierTypeSelectWrapper = styled.div`
    margin-left: 0.25em;
`;

export default function LicenceQuantitySelector({
    licenceType,
    onChange,
    onSelectOpenChange,
    invertSelectColor,
    invertContentColors,
}: {
    licenceType: LicenceType;
    onChange: (tier: Tier) => void;
    onSelectOpenChange?: (open: boolean, tier: Tier) => void;
    invertSelectColor?: boolean;
    invertContentColors?: boolean;
}): React.ReactElement {
    const cartQuery = useCartQuery();
    const cart = cartQuery?.data?.cart;
    const tieredLicences = useActiveTieredLicensingsForLicenceType(licenceType);
    const [selectedTieredLicence, setSelectedTieredLicence] =
        React.useState<TieredLicence>(tieredLicences[0]);

    const selectedTier =
        cart?.licenceTiers.find(
            (cartTier) => cartTier.tier.licenceType.id === licenceType.id,
        )?.tier || selectedTieredLicence.tiers[0];

    // Follow the tiered licence of the tier in the cart. Adjusting state
    // during render, rather than in an effect, avoids rendering a tier that
    // isn't in the selected tiered licence.
    if (selectedTieredLicence.id !== selectedTier.tieredLicence.id) {
        const newState = tieredLicences.find(
            (tieredLicence) =>
                tieredLicence.id === selectedTier.tieredLicence.id,
        );
        if (newState) {
            setSelectedTieredLicence(newState);
        }
    }

    const handleOnSelectOpenChange = React.useCallback(
        (open: boolean) => {
            if (!onSelectOpenChange) {
                return;
            }
            const newTier = selectedTieredLicence.tiers.find(
                (tier) => tier.id === selectedTier.id,
            );
            if (!newTier) {
                return;
            }
            onSelectOpenChange(open, newTier);
        },
        [onSelectOpenChange, selectedTier, selectedTieredLicence],
    );

    return (
        <Container $invertSelectColor={invertSelectColor}>
            <Select
                value={selectedTier.id}
                onOpenChange={handleOnSelectOpenChange}
                onValueChange={(value): void => {
                    const newTier = selectedTieredLicence.tiers.find(
                        (tier) => tier.id === value,
                    );
                    if (!newTier) {
                        throw Error(`Invalid tier for ${value}`);
                    }
                    onChange(newTier);
                }}
                invertContentColors={invertContentColors}
            >
                {selectedTieredLicence.tiers
                    // Exclude Unlimited tiers, they can only be administered via the CMS.
                    .filter(notUnlimited)
                    .map(
                        (tier): React.ReactElement => (
                            <SelectItem key={tier.id} value={tier.id}>
                                {tieredLicences.length > 1
                                    ? tier.amount
                                    : tier.title}
                            </SelectItem>
                        ),
                    )}
            </Select>
            {
                // If there are multiple tier types (e.g. like "Audience size" and "Budget" for Broadcast)
                // then we will present two select lists: 1 for tier type, 1 for tier. The tiers will update
                // when the tier type changes.
                tieredLicences.length > 1 && (
                    <TierTypeSelectWrapper>
                        <Select
                            value={selectedTieredLicence.id}
                            invertContentColors={invertContentColors}
                            onOpenChange={handleOnSelectOpenChange}
                            onValueChange={(value): void => {
                                const newTieredLicence = tieredLicences.find(
                                    (tieredLicence) =>
                                        tieredLicence.id === value,
                                );
                                if (
                                    newTieredLicence &&
                                    newTieredLicence.id !==
                                        selectedTieredLicence.id
                                ) {
                                    setSelectedTieredLicence(newTieredLicence);
                                    // Select corresponding tier with same multiplier or same amount, if it exists (otherwise just the first tier)
                                    const newTier =
                                        newTieredLicence.tiers.find(
                                            (tier) =>
                                                tier.multiplier ===
                                                selectedTier.multiplier,
                                        ) ||
                                        newTieredLicence.tiers.find(
                                            (tier) =>
                                                tier.amount ===
                                                selectedTier.amount,
                                        ) ||
                                        newTieredLicence.tiers[0];
                                    onChange(newTier);
                                }
                            }}
                        >
                            {tieredLicences.map((tieredLicence) => {
                                return (
                                    <SelectItem
                                        key={tieredLicence.id}
                                        value={tieredLicence.id}
                                    >
                                        {tieredLicence.unitStrPlural}
                                    </SelectItem>
                                );
                            })}
                        </Select>
                    </TierTypeSelectWrapper>
                )
            }
        </Container>
    );
}
